import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';

import { Button, Container, Stack } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetAudienceAdminList } from '~/hooks/apis/promo/useGetAudienceAdminList';
import { useListAudienceUsers } from '~/hooks/apis/promo/useListAudienceUsers';
import useCurrentBrand from '~/hooks/useCurrentBrand';

import AudienceTesterWidget from './AudienceTesterWidget';
import AudiencesListTable from './AudiencesListTable';

function getAudienceEditPath(audienceId: unknown): string | undefined {
  if (typeof audienceId === 'string' || typeof audienceId === 'number') {
    return `/marketing/audiences/${audienceId}/edit`;
  }

  return undefined;
}

export default function AudiencesPage() {
  const brand = useCurrentBrand();
  const audienceQuery = { brand, enabled: 'all' as const, page: 0, pageSize: 100, sortOrder: 'asc' } as const;
  const audienceUsersQuery = { brand, enabled: 'all' as const, page: 0, pageSize: 100 } as const;

  const audiences = useGetAudienceAdminList({ query: audienceQuery });
  const audienceUsers = useListAudienceUsers({ query: audienceUsersQuery });

  const audienceRows = audiences.data?.result?.audienceList ?? [];
  const audienceUserRows = audienceUsers.data?.result?.audienceUsers ?? [];

  const audienceColumns = useMemo(
    () => [
      { key: 'audienceId', label: 'Audience ID' },
      { key: 'audienceName', label: 'Audience name' },
      { key: 'enabled', label: 'Enabled' },
      { key: 'audienceType', label: 'Audience type' },
      { key: 'syncModel', label: 'Sync model' },
      { key: 'createdBy', label: 'Created by' },
      { key: 'createdAt', label: 'Created at' },
      { key: 'updatedAt', label: 'Updated at' },
    ],
    [],
  );

  const audienceUserColumns = useMemo(
    () => [
      { key: 'audienceId', label: 'Audience ID' },
      { key: 'userId', label: 'User ID' },
      { key: 'email', label: 'Email' },
      { key: 'createdAt', label: 'Created at' },
    ],
    [],
  );

  return (
    <>
      <Helmet>
        <title>Marketing / Audiences</title>
      </Helmet>
      <Container maxWidth="xl">
        <PageHeader title="Audiences">
          <Button component={Link} to="/marketing/audiences/create" variant="contained">
            Create audience
          </Button>
        </PageHeader>
        <Stack spacing={3}>
          <AudiencesListTable
            title="Audiences"
            description="All admin promo audiences."
            rows={audienceRows}
            isLoading={audiences.isLoading}
            isError={audiences.isError}
            errorMessage={audiences.error instanceof Error ? audiences.error.message : 'Failed to load audiences'}
            emptyMessage="No audiences found."
            columns={audienceColumns}
            getRowLabelPath={(row) => getAudienceEditPath(row.audienceId)}
          />
          <AudiencesListTable
            title="Audience Users"
            description="Users assigned to an audience."
            rows={audienceUserRows}
            isLoading={audienceUsers.isLoading}
            isError={audienceUsers.isError}
            errorMessage={
              audienceUsers.error instanceof Error ? audienceUsers.error.message : 'Failed to load audience users'
            }
            emptyMessage="No audience users found."
            columns={audienceUserColumns}
          />
          <AudienceTesterWidget />
        </Stack>
      </Container>
    </>
  );
}
